import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Collapse, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
    KeyboardArrowDownRounded,
    KeyboardArrowUpRounded,
    NotificationsOutlined,
} from "@mui/icons-material";
import { colors } from "../../theme/colors";
import ToggleButton from "../common/ToggleButton";
import { toast } from "../../universal/toast";
import utils from "../../universal/utils";
import {
    setMatchAcceptSoundMuted,
    setMatchFoundSoundMuted,
} from "../../universal/matchNotificationSounds";
import {
    ACCOUNT_PAGE_MAIN_CONTENT_SX,
    ACCOUNT_PAGE_SECTION_HEADER_SX,
    ACCOUNT_PAGE_SECTION_SX,
    ACCOUNT_PAGE_TITLE_SX,
    ACCOUNT_PAGE_SUBSECTION_ARROW_SX,
    ACCOUNT_PAGE_SUBSECTION_TOGGLE_SX,
} from "./accountPageStyles";

const basePath = utils.getEdgeBasePath();

const COLLAPSE_TIMEOUT = { enter: 320, exit: 260 };
const COLLAPSE_EASING = {
    enter: "cubic-bezier(0.22, 1, 0.36, 1)",
    exit: "cubic-bezier(0.4, 0, 0.2, 1)",
};

const MASTER_BAND_SX = {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "stretch", sm: "center" },
    justifyContent: "space-between",
    gap: { xs: "0.95rem", sm: "1.5rem" },
    width: "100%",
    mt: "1.75rem",
    px: { xs: "0.95rem", sm: "1.15rem" },
    py: { xs: "0.95rem", sm: "1.15rem" },
    borderRadius: "12px",
    border: "1px solid rgba(255,255,255,0.06)",
    background: `
        linear-gradient(
            165deg,
            rgba(18,18,22,0.92) 0%,
            rgba(12,12,14,0.96) 52%,
            rgba(10,10,12,0.98) 100%
        )
    `,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
    position: "relative",
    overflow: "hidden",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: "10%",
        right: "10%",
        height: "1px",
        background: `linear-gradient(90deg, transparent, ${alpha(colors.primary.main, 0.35)}, transparent)`,
        pointerEvents: "none",
    },
};

const BELL_WRAP_SX = (enabled) => ({
    width: { xs: 38, sm: 52 },
    height: { xs: 38, sm: 52 },
    borderRadius: { xs: "10px", sm: "14px" },
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    background: enabled
        ? alpha(colors.state.success.main, 0.13)
        : "rgba(255,255,255,0.03)",
    border: `1px solid ${
        enabled ? alpha(colors.state.success.main, 0.35) : colors.border.default
    }`,
    color: enabled ? colors.state.success.main : colors.text.muted,
    transition:
        "background 0.2s ease-in-out, border-color 0.2s ease-in-out, color 0.2s ease-in-out",
});

const SECTION_TITLE_SX = {
    color: colors.text.primary,
    fontSize: { xs: "1.05rem", sm: "1.15rem" },
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: "0.01em",
};

const SECTION_SUBTITLE_SX = {
    color: colors.text.muted,
    fontSize: "0.82rem",
    fontWeight: 400,
    lineHeight: 1.4,
    mt: "0.35rem",
};

const COLUMN_HEAD_SX = {
    display: "grid",
    gridTemplateColumns: "1fr 4.5rem",
    alignItems: "end",
    gap: "0.5rem",
    pb: "0.65rem",
};

const NOTIF_ROW_SX = {
    display: "grid",
    gridTemplateColumns: "1fr 4.5rem",
    alignItems: "center",
    gap: "0.5rem",
    py: "0.95rem",
    borderTop: `1px solid ${colors.border.subtle}`,
};

function readEmailPreferences(user) {
    const preferences = user?.emailNotificationPreferences || {};
    return {
        emailEnabled: preferences.nonTransactionalOptOut !== true,
        queueOpenEnabled: preferences.queueOpenOptOut !== true,
    };
}

function readSoundPreferences(user) {
    const preferences = user?.soundPreferences || {};
    return {
        matchFoundEnabled: preferences.matchFoundMuted !== true,
        matchAcceptEnabled: preferences.matchAcceptMuted !== true,
    };
}

function preferenceToastMessage(label, enabled) {
    return `${label} turned ${enabled ? "on" : "off"}`;
}

function NotificationsCollapsibleSection({
    id,
    title,
    subtitle,
    children,
    defaultOpen = true,
}) {
    const [open, setOpen] = useState(defaultOpen);
    const contentId = `${id}-content`;
    const ArrowIcon = open ? KeyboardArrowUpRounded : KeyboardArrowDownRounded;

    return (
        <Box
            sx={{
                width: "100%",
                borderTop: `1px solid ${colors.border.subtle}`,
                pt: "1.5rem",
                pb: open ? 0 : "0.25rem",
            }}
        >
            <Typography component="h2" sx={{ m: 0, p: 0 }}>
                <Box
                    component="button"
                    type="button"
                    aria-expanded={open}
                    aria-controls={contentId}
                    onClick={() => setOpen((current) => !current)}
                    sx={ACCOUNT_PAGE_SUBSECTION_TOGGLE_SX}
                >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box component="span" sx={SECTION_TITLE_SX}>
                            {title}
                        </Box>
                        {subtitle ? (
                            <Typography
                                component="span"
                                sx={{ ...SECTION_SUBTITLE_SX, display: "block" }}
                            >
                                {subtitle}
                            </Typography>
                        ) : null}
                    </Box>
                    <ArrowIcon aria-hidden sx={ACCOUNT_PAGE_SUBSECTION_ARROW_SX} />
                </Box>
            </Typography>
            <Collapse in={open} timeout={COLLAPSE_TIMEOUT} easing={COLLAPSE_EASING}>
                <Box id={contentId} sx={{ pt: "1.1rem", pb: "1.75rem" }}>
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}

NotificationsCollapsibleSection.propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    children: PropTypes.node,
    defaultOpen: PropTypes.bool,
};

export default function NotificationsPage({ user, actions }) {
    const { emailEnabled, queueOpenEnabled } = readEmailPreferences(user);
    const serverSounds = readSoundPreferences(user);
    const [matchFoundEnabled, setMatchFoundEnabled] = useState(
        serverSounds.matchFoundEnabled,
    );
    const [matchAcceptEnabled, setMatchAcceptEnabled] = useState(
        serverSounds.matchAcceptEnabled,
    );
    const [saving, setSaving] = useState(false);
    const [soundSaving, setSoundSaving] = useState(false);
    const [error, setError] = useState("");
    const [soundError, setSoundError] = useState("");

    useEffect(() => {
        setMatchFoundEnabled(serverSounds.matchFoundEnabled);
        setMatchAcceptEnabled(serverSounds.matchAcceptEnabled);
    }, [serverSounds.matchFoundEnabled, serverSounds.matchAcceptEnabled]);

    const handlePreferenceChange = useCallback(
        async (key, checked) => {
            if (!user?.userId || saving) return;

            const currentPreferences = user.emailNotificationPreferences || {};
            const nextPreferences = {
                nonTransactionalOptOut:
                    currentPreferences.nonTransactionalOptOut === true,
                queueOpenOptOut: currentPreferences.queueOpenOptOut === true,
                [key]: !checked,
            };

            setSaving(true);
            setError("");
            try {
                const res = await fetch(`${basePath}/api/v1/account`, {
                    method: "POST",
                    credentials: "include",
                    headers: { "content-type": "application/json" },
                    body: JSON.stringify({
                        data: {
                            userId: user.userId,
                            emailNotificationPreferences: nextPreferences,
                        },
                    }),
                });
                const response = await res.json().catch(() => null);
                if (!res.ok || response?.ok === false) {
                    throw new Error(
                        response?.message || "Failed to update email preferences",
                    );
                }
                if (typeof actions?.refreshSessionUser === "function") {
                    await actions.refreshSessionUser();
                }
                toast.success(
                    preferenceToastMessage(
                        key === "queueOpenOptOut"
                            ? "Queue opened emails"
                            : "Email notifications",
                        checked,
                    ),
                );
            } catch (err) {
                const message =
                    err?.message ||
                    "Failed to update email preferences. Please try again.";
                setError(message);
                toast.error(message);
            } finally {
                setSaving(false);
            }
        },
        [actions, saving, user],
    );

    const handleSoundPreferenceChange = useCallback(
        async (key, enabled) => {
            if (!user?.userId || soundSaving) return;

            const nextMuted = !enabled;
            if (key === "matchFoundMuted") {
                setMatchFoundEnabled(enabled);
                setMatchFoundSoundMuted(nextMuted);
            } else if (key === "matchAcceptMuted") {
                setMatchAcceptEnabled(enabled);
                setMatchAcceptSoundMuted(nextMuted);
            }

            setSoundSaving(true);
            setSoundError("");
            try {
                const res = await fetch(`${basePath}/api/v1/account`, {
                    method: "POST",
                    credentials: "include",
                    headers: { "content-type": "application/json" },
                    body: JSON.stringify({
                        data: {
                            userId: user.userId,
                            soundPreferences: { [key]: nextMuted },
                        },
                    }),
                });
                const response = await res.json().catch(() => null);
                if (!res.ok || response?.ok === false) {
                    throw new Error(
                        response?.message || "Failed to update sound preferences",
                    );
                }
                if (typeof actions?.refreshSessionUser === "function") {
                    await actions.refreshSessionUser();
                }
                toast.success(
                    preferenceToastMessage(
                        key === "matchAcceptMuted"
                            ? "Player accepted sound"
                            : "Match found sound",
                        enabled,
                    ),
                );
            } catch (err) {
                if (key === "matchFoundMuted") {
                    setMatchFoundEnabled(!enabled);
                    setMatchFoundSoundMuted(!nextMuted);
                } else if (key === "matchAcceptMuted") {
                    setMatchAcceptEnabled(!enabled);
                    setMatchAcceptSoundMuted(!nextMuted);
                }
                const message =
                    err?.message ||
                    "Failed to update sound preferences. Please try again.";
                setSoundError(message);
                toast.error(message);
            } finally {
                setSoundSaving(false);
            }
        },
        [actions, soundSaving, user],
    );

    const queueToggleActive = emailEnabled && queueOpenEnabled;

    return (
        <Box sx={ACCOUNT_PAGE_MAIN_CONTENT_SX}>
            <Box sx={ACCOUNT_PAGE_SECTION_SX}>
                <Box sx={ACCOUNT_PAGE_SECTION_HEADER_SX}>
                    <Typography
                        component="h1"
                        sx={ACCOUNT_PAGE_TITLE_SX}
                    >
                        Notifications
                    </Typography>
                </Box>

                <Box sx={MASTER_BAND_SX} aria-label="Email notifications master switch">
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: { xs: "flex-start", sm: "center" },
                            gap: { xs: "0.7rem", sm: "1rem" },
                            minWidth: 0,
                            flex: 1,
                        }}
                    >
                        <Box sx={BELL_WRAP_SX(emailEnabled)}>
                            <NotificationsOutlined
                                sx={{ fontSize: { xs: "1.1rem", sm: "1.5rem" } }}
                            />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                                sx={{
                                    color: colors.text.primary,
                                    fontSize: { xs: "0.92rem", sm: "1.02rem" },
                                    fontWeight: 700,
                                    lineHeight: 1.3,
                                }}
                            >
                                Email notifications
                            </Typography>
                            <Typography
                                sx={{
                                    color: colors.text.secondary,
                                    fontSize: { xs: "0.82rem", sm: "0.85rem" },
                                    lineHeight: 1.5,
                                    mt: "0.3rem",
                                    maxWidth: "28rem",
                                }}
                            >
                                Turns off every non-essential email at once.{" "}
                                <Box
                                    component="span"
                                    sx={{
                                        color: colors.text.primary,
                                        fontWeight: 600,
                                    }}
                                >
                                    Account, security, and payment emails are always
                                    sent
                                </Box>
                                , regardless of this setting.
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: { xs: "space-between", sm: "flex-end" },
                            gap: "0.85rem",
                            flexShrink: 0,
                            alignSelf: { xs: "stretch", sm: "center" },
                        }}
                    >
                        <Typography
                            sx={{
                                color: colors.text.secondary,
                                fontSize: "0.78rem",
                                fontWeight: 600,
                                letterSpacing: "0.02em",
                            }}
                        >
                            {emailEnabled ? "On" : "Off"}
                        </Typography>
                        <ToggleButton
                            active={emailEnabled}
                            disabled={saving}
                            onClick={() =>
                                handlePreferenceChange(
                                    "nonTransactionalOptOut",
                                    !emailEnabled,
                                )
                            }
                        />
                    </Box>
                </Box>

                {error ? (
                    <Typography
                        role="alert"
                        sx={{
                            mt: "0.85rem",
                            color: colors.state.danger.main,
                            fontSize: "0.78rem",
                            lineHeight: 1.45,
                        }}
                    >
                        {error}
                    </Typography>
                ) : null}

                <Box sx={{ mt: "1.75rem" }}>
                    <NotificationsCollapsibleSection
                        id="queue-notifications"
                        title="Queue notifications"
                        subtitle="Get notified when a queue opens"
                    >
                        <Box sx={COLUMN_HEAD_SX}>
                            <span />
                            <Typography
                                component="span"
                                sx={{
                                    textAlign: "center",
                                    fontSize: "0.68rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.08em",
                                    textTransform: "uppercase",
                                    color: colors.text.muted,
                                }}
                            >
                                Email
                            </Typography>
                        </Box>

                        <Box sx={NOTIF_ROW_SX}>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        color: colors.text.primary,
                                        fontSize: "0.9rem",
                                        fontWeight: 600,
                                        lineHeight: 1.3,
                                    }}
                                >
                                    Queue opened
                                </Typography>
                                <Typography
                                    sx={{
                                        color: colors.text.muted,
                                        fontSize: "0.79rem",
                                        lineHeight: 1.45,
                                        mt: "0.2rem",
                                        maxWidth: "22.5rem",
                                    }}
                                >
                                    When a match queue opens and becomes available.
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    opacity: emailEnabled ? 1 : 0.35,
                                    transition: "opacity 0.15s ease-in-out",
                                }}
                            >
                                <ToggleButton
                                    active={queueToggleActive}
                                    disabled={saving || !emailEnabled}
                                    onClick={() =>
                                        handlePreferenceChange(
                                            "queueOpenOptOut",
                                            !queueOpenEnabled,
                                        )
                                    }
                                />
                            </Box>
                        </Box>
                    </NotificationsCollapsibleSection>

                    <NotificationsCollapsibleSection
                        id="match-sounds"
                        title="Match sounds"
                        subtitle="Audio cues when a match is found and accepted"
                    >
                        <Box sx={COLUMN_HEAD_SX}>
                            <span />
                            <Typography
                                component="span"
                                sx={{
                                    textAlign: "center",
                                    fontSize: "0.68rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.08em",
                                    textTransform: "uppercase",
                                    color: colors.text.muted,
                                }}
                            >
                                Sound
                            </Typography>
                        </Box>

                        <Box sx={NOTIF_ROW_SX}>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        color: colors.text.primary,
                                        fontSize: "0.9rem",
                                        fontWeight: 600,
                                        lineHeight: 1.3,
                                    }}
                                >
                                    Match found
                                </Typography>
                                <Typography
                                    sx={{
                                        color: colors.text.muted,
                                        fontSize: "0.79rem",
                                        lineHeight: 1.45,
                                        mt: "0.2rem",
                                        maxWidth: "22.5rem",
                                    }}
                                >
                                    Plays a sound when an opponent is found for your
                                    match.
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                }}
                            >
                                <ToggleButton
                                    active={matchFoundEnabled}
                                    disabled={soundSaving}
                                    onClick={() =>
                                        handleSoundPreferenceChange(
                                            "matchFoundMuted",
                                            !matchFoundEnabled,
                                        )
                                    }
                                />
                            </Box>
                        </Box>

                        <Box sx={NOTIF_ROW_SX}>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        color: colors.text.primary,
                                        fontSize: "0.9rem",
                                        fontWeight: 600,
                                        lineHeight: 1.3,
                                    }}
                                >
                                    Player accepted
                                </Typography>
                                <Typography
                                    sx={{
                                        color: colors.text.muted,
                                        fontSize: "0.79rem",
                                        lineHeight: 1.45,
                                        mt: "0.2rem",
                                        maxWidth: "22.5rem",
                                    }}
                                >
                                    Plays a sound when a player accepts the match.
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                }}
                            >
                                <ToggleButton
                                    active={matchAcceptEnabled}
                                    disabled={soundSaving}
                                    onClick={() =>
                                        handleSoundPreferenceChange(
                                            "matchAcceptMuted",
                                            !matchAcceptEnabled,
                                        )
                                    }
                                />
                            </Box>
                        </Box>

                        {soundError ? (
                            <Typography
                                role="alert"
                                sx={{
                                    mt: "0.85rem",
                                    color: colors.state.danger.main,
                                    fontSize: "0.78rem",
                                    lineHeight: 1.45,
                                }}
                            >
                                {soundError}
                            </Typography>
                        ) : null}
                    </NotificationsCollapsibleSection>
                </Box>
            </Box>
        </Box>
    );
}

NotificationsPage.propTypes = {
    user: PropTypes.object,
    actions: PropTypes.object,
};
